/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DialogContent,
  Button,
  Grid,
  TextField,
  CircularProgress,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { toast } from 'react-toastify';

import api from '../../../services/api';
import { handleErrorRequest } from '../../../utils/errorsValidation';
import { useMessageBox } from '../../../context/MessageBox';
import colors from '../../../styles/settings/colors';
import fontSize from '../../../styles/settings/fontSize';
import * as S from '../../../components/ConfirmationBox/styles';

function ModalConfirmarImportacaoBluesoft({
  open,
  tipo, // 'full' | 'complementar'
  vinculos,
  onClose,
  onSuccess,
}) {
  const { setMessageData } = useMessageBox();

  const vinculosSafe = useMemo(
    () => (Array.isArray(vinculos) ? vinculos : []),
    [vinculos],
  );

  const tipoLabel = tipo === 'full' ? 'Carga Full' : 'Carga Complementar';
  const tipoPalavra = tipo === 'full' ? 'full' : 'complementar';

  const [selectedId, setSelectedId] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [rateLimit, setRateLimit] = useState(null); // { intervaloMinimoMinutos, proximaGeracaoPermitidaFull|Complementar, ... }
  const [loadingRateLimit, setLoadingRateLimit] = useState(false);
  const [rateLimitErro, setRateLimitErro] = useState(null); // string com mensagem quando GET falha
  const [rateLimitTentativa, setRateLimitTentativa] = useState(0); // trigger de retry

  useEffect(() => {
    if (!open) return;
    setConfirmText('');
    setLoading(false);
    setRateLimit(null);
    setRateLimitErro(null);
    setRateLimitTentativa(0);
    if (vinculosSafe.length === 1) {
      setSelectedId(String(vinculosSafe[0].ancoraImportacaoTituloApiErpId));
    } else {
      setSelectedId('');
    }
  }, [open, vinculosSafe]);

  useEffect(() => {
    if (!open || !selectedId) {
      setRateLimit(null);
      setRateLimitErro(null);
      return;
    }
    let cancelado = false;
    (async () => {
      setLoadingRateLimit(true);
      setRateLimitErro(null);
      try {
        const r = await api.get(`ImportacaoTitulos/${selectedId}/RateLimitInfo`);
        if (cancelado) return;
        const resp = r?.data?.resposta;
        if (r?.data?.status === 'Inconsistencia' || !resp) {
          const msg = r?.data?.resposta?.[0]?.message || 'Resposta invalida do servidor.';
          setRateLimit(null);
          setRateLimitErro(msg);
        } else {
          setRateLimit(resp);
        }
      } catch (e) {
        if (cancelado) return;
        const status = e?.response?.status;
        const msg =
          e?.response?.data?.resposta?.[0]?.message ||
          e?.message ||
          'Falha de rede ao consultar rate-limit.';
        setRateLimit(null);
        setRateLimitErro(status ? `HTTP ${status}: ${msg}` : msg);
      } finally {
        if (!cancelado) setLoadingRateLimit(false);
      }
    })();
    return () => { cancelado = true; };
  }, [open, selectedId, rateLimitTentativa]);

  const emExecucao = !!rateLimit?.emExecucaoDesde;

  const proximaPermitida = useMemo(() => {
    if (!rateLimit) return null;
    return tipo === 'full'
      ? rateLimit.proximaGeracaoPermitidaFull
      : rateLimit.proximaGeracaoPermitidaComplementar;
  }, [rateLimit, tipo]);

  const bloqueadoPorRateLimit = !!proximaPermitida && new Date(proximaPermitida).getTime() > Date.now();

  useEffect(() => {
    if (!open || !selectedId) return undefined;
    if (!emExecucao && !bloqueadoPorRateLimit) return undefined;
    const id = setInterval(() => setRateLimitTentativa((n) => n + 1), 20000);
    return () => clearInterval(id);
  }, [open, selectedId, emExecucao, bloqueadoPorRateLimit]);

  const proximaFormatada = useMemo(() => {
    if (!proximaPermitida) return null;
    const d = new Date(proximaPermitida);
    if (Number.isNaN(d.getTime())) return null;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, [proximaPermitida]);

  const minutosRestantes = useMemo(() => {
    if (!proximaPermitida) return 0;
    const ms = new Date(proximaPermitida).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 60000));
  }, [proximaPermitida]);

  const podeConfirmar =
    !loading &&
    !loadingRateLimit &&
    !bloqueadoPorRateLimit &&
    !emExecucao &&
    !rateLimitErro &&
    vinculosSafe.length > 0 &&
    !!selectedId &&
    confirmText.trim().toLowerCase() === tipoPalavra;

  const handleConfirmar = async () => {
    const vinculo = vinculosSafe.find(
      (v) => String(v.ancoraImportacaoTituloApiErpId) === String(selectedId),
    );
    if (!vinculo) {
      toast.error('Selecione uma integracao Bluesoft.');
      return;
    }
    const rota = tipo === 'full' ? 'GerarFull' : 'GerarComplementar';
    const url = `ImportacaoTitulos/${vinculo.ancoraImportacaoTituloApiErpId}/${rota}`;
    try {
      setLoading(true);
      // skipGlobalErrorHandler: o interceptor global (api.js) dispara handleErrorRequest sozinho
      // pra qualquer resposta != 2xx, ANTES do catch abaixo rodar — precisamos decidir aqui se o
      // 409 e "titulo inconsistente" (nao mostra erro) ou erro real (mostra), entao assumimos o
      // tratamento completo e desligamos o automatico.
      const response = await api.post(url, undefined, { skipGlobalErrorHandler: true });
      setMessageData(response?.data?.complemento?.mensagensBox);
      const n = response?.data?.resposta?.duplicatasProcessadas ?? 0;
      toast.success(`${n} duplicatas encontradas e aguardando processamento.`);
      if (onSuccess) onSuccess(response.data);
      onClose && onClose();
    } catch (error) {
      const status = error?.response?.status;
      const mensagens = (error?.response?.data?.resposta || [])
        .map((n) => n?.message)
        .filter(Boolean);
      const msg = mensagens[0] || error?.response?.data?.message || 'Falha ao disparar a importacao.';

      // Titulos inconsistentes (ex.: "Nenhum título válido para gerar o arquivo...") sao um
      // resultado esperado da consulta ao ERP, nao um erro do sistema — nao mostra a caixa de erro
      // nem o texto tecnico "corrija no ERP", so um aviso neutro com o total (0). Erros reais de
      // conexao/integracao (rate-limit, arquivo anterior ainda processando, perfil nao encontrado,
      // ERP inacessivel etc.) continuam aparecendo normalmente.
      const ehTitulosInconsistentes = status === 409 && mensagens.some((m) => m.toLowerCase().includes('título'));

      if (ehTitulosInconsistentes) {
        toast.info('0 duplicatas encontradas e aguardando processamento.');
      } else if (status === 429 || status === 409) {
        toast.warn(msg);
        setRateLimitTentativa((n) => n + 1);
        setMessageData(error?.response?.data?.complemento?.mensagensBox);
      } else {
        toast.error(msg);
        setMessageData(error?.response?.data?.complemento?.mensagensBox);
        handleErrorRequest(error?.response);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <S.DialogModal
      open={!!open}
      onClose={onClose}
      disableEscapeKeyDown
      className="dialog-parent"
      widthContainer={560}
    >
      <S.DialogContainer>
        <div className="margin-container">
          <S.Title style={{ textTransform: 'uppercase' }}>
            Disparar {tipoLabel} (Bluesoft)
          </S.Title>
          <DialogContent
            dividers
            style={{
              fontFamily:
                "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
              fontSize: fontSize.button,
              textTransform: 'uppercase',
            }}
          >
        {vinculosSafe.length === 0 ? (
          <div style={{ padding: '8px 0' }}>
            Nenhuma integracao Bluesoft ativa para esta rede.
          </div>
        ) : (
          <Grid container spacing={2}>
            {vinculosSafe.length > 1 && (
              <Grid item xs={12}>
                <label style={{ fontSize: 12, color: '#555' }}>
                  Integracao Bluesoft
                </label>
                <Autocomplete
                  options={vinculosSafe}
                  getOptionLabel={(v) =>
                    v.apelido || v.perfilNome || v.nomePerfilErp || `Integracao #${v.ancoraImportacaoTituloApiErpId}`
                  }
                  getOptionSelected={(opt, val) =>
                    opt.ancoraImportacaoTituloApiErpId === val.ancoraImportacaoTituloApiErpId
                  }
                  value={vinculosSafe.find((v) => String(v.ancoraImportacaoTituloApiErpId) === String(selectedId)) || null}
                  onChange={(_, selected) => setSelectedId(selected ? String(selected.ancoraImportacaoTituloApiErpId) : '')}
                  noOptionsText="Nenhuma integração encontrada"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Digite para buscar..."
                      variant="outlined"
                      size="small"
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                Voce esta prestes a disparar uma <b>{tipoLabel}</b> manualmente.
                Para confirmar, digite <b>{tipoPalavra}</b> abaixo.
              </div>

              {loadingRateLimit && (
                <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                  Consultando rate-limit...
                </div>
              )}
              {!loadingRateLimit && !rateLimitErro && emExecucao && (
                <div
                  style={{
                    padding: '8px 10px',
                    marginBottom: 8,
                    borderRadius: 4,
                    background: '#FFF3CD',
                    color: '#8A6D3B',
                    border: '1px solid #FAEBCC',
                    fontSize: 13,
                  }}
                >
                  <b>Já em processamento.</b> Você disparou uma carga {rateLimit.tipoEmExecucao} às{' '}
                  <b>{new Date(rateLimit.emExecucaoDesde).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</b>
                  {' '}e ela ainda está sendo processada. Só é possível disparar novamente após a conclusão.
                </div>
              )}
              {!loadingRateLimit && rateLimitErro && (
                <div
                  style={{
                    padding: '8px 10px',
                    marginBottom: 8,
                    borderRadius: 4,
                    background: '#F2DEDE',
                    color: '#A94442',
                    border: '1px solid #EBCCD1',
                    fontSize: 13,
                  }}
                >
                  <b>Nao foi possivel verificar rate-limit.</b> {rateLimitErro}
                  <div style={{ marginTop: 6 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setRateLimitTentativa((n) => n + 1)}
                    >
                      Tentar novamente
                    </Button>
                    <span style={{ fontSize: 11, color: '#A94442', marginLeft: 8 }}>
                      Botao Confirmar bloqueado enquanto rate-limit nao for verificado.
                    </span>
                  </div>
                </div>
              )}
              {!loadingRateLimit && !rateLimitErro && !emExecucao && bloqueadoPorRateLimit && (
                <div
                  style={{
                    padding: '8px 10px',
                    marginBottom: 8,
                    borderRadius: 4,
                    background: '#FFF3CD',
                    color: '#8A6D3B',
                    border: '1px solid #FAEBCC',
                    fontSize: 13,
                  }}
                >
                  <b>Rate-limit ativo.</b> Proxima {tipoLabel.toLowerCase()} permitida em{' '}
                  <b>{proximaFormatada}</b> (faltam {minutosRestantes} min).
                  {rateLimit && (
                    <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                      Intervalo minimo configurado: {rateLimit.intervaloMinimoMinutos} min.
                    </div>
                  )}
                </div>
              )}
              {!loadingRateLimit && !rateLimitErro && !emExecucao && !bloqueadoPorRateLimit && rateLimit && (
                <div style={{ fontSize: 11, color: '#999', marginBottom: 8 }}>
                  Intervalo minimo: {rateLimit.intervaloMinimoMinutos} min.
                  {tipo === 'full' && rateLimit.dataUltimaGeracaoFull && (
                    <> {' | '} Ultima Full ok: {new Date(rateLimit.dataUltimaGeracaoFull).toLocaleString('pt-BR')}</>
                  )}
                  {tipo === 'full' && rateLimit.dataUltimaTentativaFull && (
                    <> {' | '} Ultima tentativa Full: {new Date(rateLimit.dataUltimaTentativaFull).toLocaleString('pt-BR')}</>
                  )}
                  {tipo === 'complementar' && rateLimit.dataUltimaGeracaoComplementar && (
                    <> {' | '} Ultima Complementar ok: {new Date(rateLimit.dataUltimaGeracaoComplementar).toLocaleString('pt-BR')}</>
                  )}
                  {tipo === 'complementar' && rateLimit.dataUltimaTentativaComplementar && (
                    <> {' | '} Ultima tentativa Complementar: {new Date(rateLimit.dataUltimaTentativaComplementar).toLocaleString('pt-BR')}</>
                  )}
                </div>
              )}

              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder={`Digite "${tipoPalavra}" para confirmar`}
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                autoComplete="off"
                disabled={bloqueadoPorRateLimit || emExecucao || !!rateLimitErro || loadingRateLimit}
              />
            </Grid>
          </Grid>
        )}
          </DialogContent>
          <S.DialogActionsMessage>
            <Button
              onClick={onClose}
              disabled={loading}
              style={{ backgroundColor: colors.alto, fontWeight: 400 }}
            >
              Fechar
            </Button>
            {vinculosSafe.length > 0 && (
              <Button
                onClick={handleConfirmar}
                disabled={!podeConfirmar}
                autoFocus
                focusVisible
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : null
                }
                style={{
                  color: colors.white,
                  backgroundColor: podeConfirmar ? colors.sanMarino : '#ccc',
                  fontWeight: 400,
                  cursor: podeConfirmar ? 'pointer' : 'not-allowed',
                }}
              >
                {loading ? 'Disparando...' : 'Confirmar'}
              </Button>
            )}
          </S.DialogActionsMessage>
        </div>
      </S.DialogContainer>
    </S.DialogModal>
  );
}

export default ModalConfirmarImportacaoBluesoft;
